// Single source of truth for "what does the VENDOR still need to act on".
// Pure + unit-tested (no DOM harness). Used by the nav badge, the dashboard
// "Needs your action" section, the Questionnaire stat card, and the post-submit
// "Needs action" tab — so every count agrees.
import { isAwaitingVendor, type ReviewSubmission } from './reviewState.ts';
import { isUnansweredRequired } from './outstandingActions.ts';
import type { CompletionReason } from '@l2u/shared/progress';

export type ActionReason = 'rejected' | 'admin_comment' | 'unanswered';
export type VendorActionItem = { questionId: string; reason: ActionReason };

type ActionQuestion = { id: string; required: boolean; exclude_from_compliance: boolean };

/**
 * Whether the vendor owes an action on a single question.
 * - Admin returned it (rejected) or commented last → always an action.
 * - Required + visible + unanswered → an action as soon as the questionnaire is
 *   assigned, so a freshly invited vendor sees the nav badge / dashboard flag
 *   their outstanding questions from first login (not only after the first
 *   submit). Approved / not_applicable never count.
 */
export function needsVendorAction(
  question: ActionQuestion,
  submission: ReviewSubmission | undefined,
  completionByQuestion: Map<string, CompletionReason | null>,
): VendorActionItem | null {
  if (submission && isAwaitingVendor(submission)) {
    return {
      questionId: question.id,
      reason: submission.status === 'rejected' ? 'rejected' : 'admin_comment',
    };
  }
  // Ignore approved/not_applicable items
  if (submission && (submission.status === 'approved' || submission.status === 'not_applicable')) {
    return null;
  }
  if (isUnansweredRequired(question, completionByQuestion)) {
    return { questionId: question.id, reason: 'unanswered' };
  }
  return null;
}

/** All action items in one questionnaire's (already visible) question set.
 *  subByQ is ReadonlyMap so a `Map<string, Submission>` (a wider value type) is
 *  assignable — TS treats Map's value type as invariant, ReadonlyMap as covariant. */
export function questionnaireActionItems(
  questions: ActionQuestion[],
  subByQ: ReadonlyMap<string, ReviewSubmission>,
  completionByQuestion: Map<string, CompletionReason | null>,
): VendorActionItem[] {
  const items: VendorActionItem[] = [];
  for (const q of questions) {
    const item = needsVendorAction(q, subByQ.get(q.id), completionByQuestion);
    if (item) items.push(item);
  }
  return items;
}

/**
 * Whether the vendor has submitted this questionnaire (mirrors
 * Questionnaire.tsx selectedQuestionnaireSubmitted): company-wide submitted
 * states, or any question already reviewed (approved / N-A / rejected).
 */
export function isQuestionnaireSubmitted(
  companyStatus: string | undefined,
  questions: { id: string }[],
  subByQ: ReadonlyMap<string, { status: string }>,
): boolean {
  if (companyStatus === 'review' || companyStatus === 'changes' || companyStatus === 'compliant') {
    return true;
  }
  return questions.some((q) => {
    const st = subByQ.get(q.id)?.status;
    return st === 'approved' || st === 'not_applicable' || st === 'rejected';
  });
}

/**
 * Whether the SELECTED questionnaire should render in its post-submit view
 * (the read-only "Needs action / All questions" list) instead of the fillable
 * step wizard.
 *
 * Driven entirely by PER-ANSWER persisted signals, never by the company-wide
 * status. Submission has no per-questionnaire flag of its own, but
 * `POST /me/submit` now stamps `submitted_at` on every answer it submits, so a
 * questionnaire is "submitted" once any of its answers carries a `submitted_at`
 * (covers the window between submitting and the admin's first review, and
 * survives reloads) or has already been reviewed (approved / rejected /
 * not_applicable — those statuses only exist post-submit; kept as a defensive
 * fallback in case `submitted_at` is ever absent).
 *
 * Crucially, an unsubmitted draft answer (status 'pending', `submitted_at`
 * null) never counts — so a review/changes/compliant vendor assigned a
 * brand-new questionnaire keeps the fillable wizard while they fill it in, and
 * only flips to the read-only view after they actually submit. Completing the
 * answers is NOT submitting.
 */
export function questionnaireSubmitted(
  questions: { id: string }[],
  subByQ: ReadonlyMap<string, { status: string; submitted_at?: string | null }>,
): boolean {
  return questions.some((q) => {
    const sub = subByQ.get(q.id);
    if (!sub) return false;
    if (sub.submitted_at != null) return true;
    return (
      sub.status === 'approved' || sub.status === 'not_applicable' || sub.status === 'rejected'
    );
  });
}

/** One-line reason summary for a dashboard row. */
export function actionReasonSummary(items: VendorActionItem[]): string {
  const hasComment = items.some((i) => i.reason === 'admin_comment' || i.reason === 'rejected');
  const hasUnanswered = items.some((i) => i.reason === 'unanswered');
  if (hasComment && hasUnanswered) return 'Reply to comments & answer questions';
  if (hasComment) return 'Reply to admin comments';
  return 'Answer outstanding questions';
}
